import React, { useEffect, useState, useRef, useCallback } from 'react';

import { Button } from 'reactstrap';

import Filters from './Filters';

import DiscountModal from '~/components/DiscountModal';
import EditProductPriceModal from '~/components/EditProductPriceModal';
import ImportRulesButton from '~/components/ImportRulesButton';
import Pagination from '~/components/Pagination';
import ProductsTable from '~/components/ProductsTable';

import api from '~/services/api';

import util from '~/assets/util';
import notification from '~/services/notification';

const DEFAULT_FILTERS = {
  name: '',
  isbn: '',
  category_id: '',
  sort_by_discount: '',
  selectedOptionAuthor: [],
  selectedOptionBrand: [],
  selectedOptionCategory: [],
};

function Discounts() {
  const [deleting, setDeleting] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState(null);
  const [productId, setProductId] = useState(null);

  const loadRequest = useRef(null);

  const [filters, setFilters]: any = useState(DEFAULT_FILTERS);

  const doSearch = async (page: any = 1) => {
    if (!parseInt(page)) {
      return;
    }

    // @ts-ignore
    const cancelToken = api.CancelToken;

    const source = cancelToken.source();

    try {
      setLoading(true);

      if (loadRequest.current) {
        loadRequest.current.cancel();
      } else {
        loadRequest.current = source;
      }

      const url = `admin/products/with-discount?page=${page}`;

      const res = await api.get(url, {
        params: filters,
        cancelToken: source.token,
      });

      loadRequest.current = null;

      const { products } = res.data;

      products.links?.shift();
      products.links?.splice(products.links.length - 1, 1);

      setProducts(products);
    } catch (e) {
      console.error(e);
    } finally {
      !loadRequest.current && setLoading(false);
    }
  };

  const doDelete = useCallback(
    async (idOrIds) => {
      const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];

      try {
        setDeleting((prev) => [...prev, ...ids]);

        const data = {
          rules: ids,
        };

        await api.post('admin/products/rule/delete', data);

        notification.$s(util.t('DISCOUNT_DELETED'));
        doSearch();
      } catch (e) {
        notification.$e(e);
      } finally {
        setDeleting((prev) => prev.filter((item) => !ids.includes(item)));
      }
    },
    [deleting, doSearch],
  );

  const onSaveDiscount = () => {
    doSearch();
    setShowDiscountModal(false);
  };

  useEffect(() => {
    doSearch();
  }, []);

  return (
    <>
      <header className="row my-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <Filters
                filters={filters}
                setFilters={setFilters}
                onSave={doSearch}
                onClear={() => setFilters({ ...DEFAULT_FILTERS, clearSelect: true })}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="card">
        <section className="card-body">
          <div className="container-fluid p-3">
            <header>
              <h1 className="h2">{util.t('DISCOUNTS')}</h1>
            </header>

            <div className="text-right">
              <ImportRulesButton className="my-3 mr-1" />

              <Button
                onClick={() => setShowDiscountModal(true)}
                className="my-3 mr-1"
                color="primary"
                outline>
                {util.t('CREATE_DISCOUNT')}
              </Button>
            </div>

            <ProductsTable
              onOpen={(id) => {
                setProductId(id);
              }}
              loading={loading}
              products={products}
              enableDiscountColumns
              deleting={deleting}
              onDelete={doDelete}
              rowKey="price_rules_id"
              isMultiDelete
            />

            <div className="d-flex justify-content-end mt-4">
              {products && <Pagination onChange={(page) => doSearch(page)} {...products} />}
            </div>
          </div>
        </section>

        <DiscountModal
          isOpen={showDiscountModal}
          onToggle={() => setShowDiscountModal(false)}
          onSave={onSaveDiscount}
        />

        <EditProductPriceModal
          isOpen={productId != null}
          toggle={() => setProductId(null)}
          productId={productId}
          onClose={() => setProductId(null)}
          onSave={doSearch}
        />
      </main>
    </>
  );
}

export default Discounts;
